// Lógica pura del consentimiento y su relación con el estatus del jugador.
import { reconsentimientoVencido, GRACIA_RECONSENTIMIENTO_DIAS, isMinor } from './age'

// Requisitos para que un jugador pueda estar Activo.
// - Siempre exige CURP válida.
// - Exige consentimiento (trata_datos) SOLO si el club lo tiene activado
//   (`exigirConsentimiento`). Mientras esté apagado, la falta de consentimiento
//   no bloquea (transición previa a Fase 4).
export const evaluarActivo = ({ curpValida, trataDatos, exigirConsentimiento = false } = {}) => {
  const faltaCurp = !curpValida
  const faltaConsentimiento = !!exigirConsentimiento && !trataDatos
  return {
    ok: !faltaCurp && !faltaConsentimiento,
    faltaCurp,
    faltaConsentimiento,
  }
}

// ¿Un registro de consentimiento sigue siendo válido para tratar datos?
// - Debe estar 'firmado' y con trata_datos = true.
// - Si lo firmó el tutor y el jugador ya cumplió 18 (pasada la gracia), deja de ser
//   válido: se requiere reconsentimiento del propio jugador. EXCEPTO si el jugador
//   está bajo tutela legal (ahí el tutor sigue consintiendo aunque sea mayor).
// - Debe estar VERIFICADO: código confirmado por correo, o firma en papel que un
//   humano del club revisó. Un link abierto no prueba quién lo abrió, y eso es
//   justo lo que el abogado marcó como insuficiente para datos de menores.
export const consentimientoVerificado = (consent) => {
  if (!consent) return false
  if (consent.otp_verificado_at) return true
  return consent.metodo === 'pdf_firmado' && !!consent.verificado_at
}

// ¿Se puede usar la imagen del jugador para difusión?
//
// `usa_imagen` conserva lo que la persona autorizó el día que firmó -- eso es la
// prueba, y la prueba no se edita. La revocación es un contra-asiento aparte. Por
// eso el estado efectivo se calcula, no se lee de una sola columna.
export const puedeUsarImagen = (consent) =>
  !!consent?.usa_imagen && !consent?.imagen_revocada_at

export const consentimientoValido = (
  consent,
  { fechaNacimiento, asOf = new Date(), graceDays = GRACIA_RECONSENTIMIENTO_DIAS, bajoTutela = false } = {}
) => {
  if (!consent) return false
  if (consent.estado !== 'firmado') return false
  if (!consent.trata_datos) return false
  // OJO: esto invalida las firmas viejas con `metodo = 'digital_link'` (y las 31 sin
  // método). Es deliberado y está acordado -- se vuelven a recabar con el flujo
  // nuevo, "mejor ahora que son 61 y no mañana que sean 500".
  if (!consentimientoVerificado(consent)) return false
  if (bajoTutela) return true
  const vencido = reconsentimientoVencido({
    fechaNacimiento,
    consintioTipo: consent.consiente_tipo,
    asOf,
    graceDays,
  })
  return !vencido
}

// Datos del club sin los cuales su Aviso de Privacidad sale con "[por definir]"
// donde la ley exige que diga quien es el responsable, donde esta y a donde
// mandar una solicitud ARCO. Un Aviso asi no sirve como prueba de nada.
//
// Vive aqui, en una sola definicion, porque tres sitios necesitan la MISMA
// respuesta: el alta de la organizacion, el interruptor de "exigir
// consentimiento" y el envio de solicitudes de firma. Cuando estaba solo en la
// pantalla de configuracion, un club podia terminar exigiendo consentimiento con
// los tres campos vacios -- que es como quedo SEAHAWKS LAGUNA.
export const CAMPOS_LEGALES_AVISO = [
  ['razon_social', 'razón social'],
  ['domicilio_fiscal', 'domicilio'],
  ['correo_privacidad', 'correo para derechos ARCO'],
]

export const camposLegalesFaltantes = (org = {}) =>
  CAMPOS_LEGALES_AVISO
    .filter(([campo]) => !String(org?.[campo] || '').trim())
    .map(([, etiqueta]) => etiqueta)

export const avisoClubCompleto = (org = {}) => camposLegalesFaltantes(org).length === 0

// A QUIEN SE MANDA EL CONSENTIMIENTO.
//
// Son dos papeles distintos, y el modelo ya los separa: el responsable de PAGO cuelga
// de la familia (familia_personas); quien FIRMA cuelga del jugador (jugador_personas),
// y si el jugador es mayor de edad, firma el mismo. Pueden ser la misma persona, y
// pueden no serlo: la hija mayor que vive en la familia de sus papas firma por si
// misma aunque pague la mama. Antes el enlace iba al telefono de la familia siempre.
//
// Orden de preferencia:
//   mayor de edad  -> su telefono; si no lo hay, el tutor ligado, la ficha, la familia
//   menor / tutela -> el tutor ligado; si no lo hay, la familia, la ficha. NUNCA el suyo.
// Sin fecha de nacimiento no se asume que firma el: se va por el tutor.
export const destinoConsentimiento = ({
  fechaNacimiento = null,
  bajoTutela = false,
  telefonoJugador = '',
  telefonoTutorLegal = '',
  telefonoFamilia = '',
  telefonoContacto = '',
  asOf = new Date(),
} = {}) => {
  const limpio = (t) => String(t || '').trim()
  const firmaElJugador = isMinor(fechaNacimiento, asOf) === false && !bajoTutela
  const cadena = firmaElJugador
    ? [['jugador', telefonoJugador], ['tutor', telefonoTutorLegal], ['contacto', telefonoContacto], ['familia', telefonoFamilia]]
    : [['tutor', telefonoTutorLegal], ['familia', telefonoFamilia], ['contacto', telefonoContacto]]
  const hit = cadena.find(([, t]) => limpio(t))
  return { firmaElJugador, quien: hit ? hit[0] : null, telefono: hit ? limpio(hit[1]) : null }
}

// Si la firma vigente del jugador cubre los torneos de la red (pieza 2b, spec 2026-10-11 §4;
// si no, se avisa y no bloquea, contrato H). Espejo de _firma_cubre_torneos (migracion
// 20261012_datos_del_torneo.sql): firmada, con trata_datos, verificada y con
// documento_datos.cubre_torneos = true (la firma digital sobre el Aviso 1.1, o la de papel
// registrada con la carta 1.1; las dos guardan un booleano JSON, y la base compara con
// 'true'::jsonb: un texto 'true' no cuenta). Ademas aplica la regla del reconsentimiento a los
// 18, que la base no repite (contrato B6): aqui es mas estricta, nunca mas laxa. `consent` tiene
// que ser la fila firmada (con trata_datos) mas reciente, la misma que mira la base
// (ORDER BY fecha_hora DESC NULLS LAST, created_at DESC): ver ultimaFirmadaPorJugador en
// services/consent.js.
export const firmaCubreTorneos = (consent, opciones = {}) =>
  consentimientoValido(consent, opciones) && consent?.documento_datos?.cubre_torneos === true
